import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "August 16, 2026",
    body: [
      ["Overview",
        "Elite Mechanical Portal (\"the Portal\") is an internal business application operated by Elite Mechanical for managing projects, customers, employees, timesheets and payroll. This policy explains what information the Portal collects and how it is used."],
      ["Information we collect",
        "The Portal stores information that authorised users enter into it — including but not limited to employee names, contact details, wages, hours worked, tax deductions, customer contact information, project details, billing data, and files uploaded to a project or employee record. When you sign in with Google we also receive your Google email address, name, and profile picture from Google."],
      ["How we use the information",
        "Data stored in the Portal is used solely to operate the internal business of Elite Mechanical — scheduling work, paying employees, invoicing customers, remitting statutory deductions to the CRA, and communicating with employees and customers. We do not sell, rent or otherwise disclose the data to third parties for marketing or advertising."],
      ["Where the data lives",
        "All data is stored on a private server operated by Elite Mechanical. It is not shared with any third-party analytics, advertising, or profiling service. Backups are stored on the same server. Only authorised employees of Elite Mechanical with a Portal account and the appropriate role have access."],
      ["Cookies & local storage",
        "The Portal uses first-party cookies and browser storage (localStorage, sessionStorage, IndexedDB) to keep you signed in and remember display preferences. No third-party cookies are set."],
      ["Retention",
        "Employee and payroll records are retained for the period required by the Ontario Employment Standards Act (ESA), the Canada Revenue Agency, and any applicable Occupational Health & Safety legislation — typically a minimum of three years, longer for tax records. Data may be deleted sooner on written request from the individual concerned, subject to those retention obligations."],
      ["Your rights",
        "If you are an employee or customer whose data is stored in the Portal, you may contact Elite Mechanical at the phone number or address on our website to request a copy of the personal information we hold about you, to correct it, or to request its deletion (subject to legal retention rules)."],
      ["Changes to this policy",
        "This policy may be updated from time to time. The \"Last updated\" date at the top of this page reflects the most recent revision."],
      ["Contact",
        "For questions about privacy, contact Elite Mechanical using the contact information published on elitemechanical.solutions."],
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "August 16, 2026",
    body: [
      ["Acceptance",
        "By signing in to the Elite Mechanical Portal you agree to these Terms of Service. If you do not agree, do not sign in and do not use the Portal."],
      ["Who may use the Portal",
        "The Portal is provided for internal use by Elite Mechanical, its employees, contractors, and authorised representatives of its customers and vendors. Access is granted at the sole discretion of Elite Mechanical and may be revoked at any time."],
      ["Acceptable use",
        "You agree not to (a) attempt to gain unauthorised access to any part of the Portal or the underlying server, (b) upload material that is unlawful, defamatory, or that infringes another person's rights, (c) use the Portal to send unsolicited communications, or (d) interfere with the operation of the Portal for other users."],
      ["Account security",
        "You are responsible for keeping your account credentials confidential. Notify Elite Mechanical immediately if you suspect your account has been compromised."],
      ["Data & intellectual property",
        "All business records entered into the Portal (customer lists, project data, payroll data, uploaded files, etc.) remain the property of Elite Mechanical. You may not export or share Portal data outside of Elite Mechanical without written permission."],
      ["Service availability",
        "The Portal is provided \"as is\" without warranty of any kind. Elite Mechanical does not guarantee uninterrupted availability, freedom from errors, or fitness for a particular purpose. Scheduled maintenance and unscheduled outages may occur."],
      ["Limitation of liability",
        "To the fullest extent permitted by law, Elite Mechanical is not liable for indirect, incidental, or consequential losses arising from your use of the Portal. Your sole remedy for dissatisfaction with the Portal is to stop using it."],
      ["Termination",
        "Elite Mechanical may suspend or terminate any account, with or without notice, for any reason including violation of these Terms."],
      ["Governing law",
        "These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein."],
      ["Changes",
        "These Terms may be updated at any time. The \"Last updated\" date at the top of this page reflects the most recent revision. Continued use of the Portal after changes constitutes acceptance of the revised Terms."],
    ],
  },
};

const Legal = () => {
  const { doc } = useParams();
  // Intuit / QBO calls it "EULA" — same doc as our Terms of Service.
  const key = doc === "eula" ? "terms" : doc;
  const d = DOCS[key] || DOCS.privacy;
  return (
    <div className="min-h-screen bg-[#050505] px-4 py-10 text-white/85" data-testid={`legal-page-${doc}`}>
      <div className="mx-auto max-w-3xl">
        <Link
          to="/dashboard"
          data-testid="legal-back"
          className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50 hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8fa4f0]">Legal</p>
        <h1 className="mt-1 text-3xl font-bold uppercase tracking-tight sm:text-4xl">{d.title}</h1>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">Last updated {d.updated}</p>
        <div className="mt-8 space-y-6">
          {d.body.map(([heading, para]) => (
            <section key={heading}>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/90">{heading}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-white/70">{para}</p>
            </section>
          ))}
        </div>
        <div className="mt-10 flex gap-4 text-xs">
          <Link to="/legal/privacy" className="font-mono uppercase tracking-widest text-white/50 hover:text-white" data-testid="legal-link-privacy">Privacy</Link>
          <Link to="/legal/terms" className="font-mono uppercase tracking-widest text-white/50 hover:text-white" data-testid="legal-link-terms">Terms</Link>
        </div>
      </div>
    </div>
  );
};

export default Legal;
